import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Target, Users, MapPin, AlertTriangle, FileText, Building2, User, Clock, Eye, Download, ChevronDown, ChevronRight, List, RefreshCw, HelpCircle, Lightbulb, TrendingUp } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import publishedAuditPlanService from '../../../../../services/audit_services/publishedAuditPlanService';
import exportAuditPlanService from '../../../../../services/audit_services/export_pdf_services/exportAuditPlanService';

export default function PublishedAuditPlanDetails() {
  const { id } = useParams();
  const { authTokens, hasAnyRole } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  // Check if user has permission to view published audit plan details
  const canViewPublishedPlans = hasAnyRole(['AuditManager', 'AuditUser']);

  const [auditPlan, setAuditPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Sections state
  const [showSections, setShowSections] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);

  // Questions state - keyed by section ID
  const [sectionQuestions, setSectionQuestions] = useState({});
  const [questionsLoading, setQuestionsLoading] = useState({});
  const [showQuestions, setShowQuestions] = useState({});

  // Recommendations state - keyed by question ID
  const [questionRecommendations, setQuestionRecommendations] = useState({});
  const [recommendationsLoading, setRecommendationsLoading] = useState({});
  const [showRecommendations, setShowRecommendations] = useState({});

  // Follow-ups state - keyed by recommendation ID
  const [recommendationFollowUps, setRecommendationFollowUps] = useState({});
  const [followUpsLoading, setFollowUpsLoading] = useState({});
  const [showFollowUps, setShowFollowUps] = useState({});
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchAuditPlan();
  }, [id]);

  const fetchAuditPlan = async () => {
    if (!canViewPublishedPlans) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanDetail(authTokens.access, id);
      
      if (result.success) {
        setAuditPlan(result.data);
      } else {
        console.error('Error fetching published audit plan:', result.error);
        showError(result.error || "Erreur lors du chargement des détails du plan d'audit publié");
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching published audit plan:', error);
      showError("Erreur lors du chargement des détails du plan d'audit publié");
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    if (showSections) {
      setShowSections(false);
      return;
    }

    setSectionsLoading(true);
    setShowSections(true);
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSections(
        authTokens.access,
        id,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setSections(result.data.results || []);
      } else {
        showError(result.error || 'Erreur lors du chargement des sections');
      }
      setSectionsLoading(false);
    } catch (error) {
      console.error('Error fetching sections:', error);
      showError('Erreur lors du chargement des sections');
      setSectionsLoading(false);
    }
  };

  const refreshSections = async () => {
    setSectionsLoading(true);
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSections(
        authTokens.access,
        id,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setSections(result.data.results || []);
      } else {
        showError(result.error || 'Erreur lors de l’actualisation des sections');
      }
      setSectionsLoading(false);
    } catch (error) {
      console.error('Error refreshing sections:', error);
      showError('Erreur lors de l’actualisation des sections');
      setSectionsLoading(false);
    }
  };

  const fetchQuestions = async (sectionId) => {
    if (showQuestions[sectionId]) {
      setShowQuestions(prev => ({ ...prev, [sectionId]: false }));
      return;
    }

    setQuestionsLoading(prev => ({ ...prev, [sectionId]: true }));
    setShowQuestions(prev => ({ ...prev, [sectionId]: true }));
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSectionQuestions(
        authTokens.access,
        id,
        sectionId,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setSectionQuestions(prev => ({
          ...prev,
          [sectionId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Erreur lors du chargement des questions');
      }
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    } catch (error) {
      console.error('Error fetching questions:', error);
      showError('Erreur lors du chargement des questions');
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  const refreshQuestions = async (sectionId) => {
    setQuestionsLoading(prev => ({ ...prev, [sectionId]: true }));
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSectionQuestions(
        authTokens.access,
        id,
        sectionId,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setSectionQuestions(prev => ({
          ...prev,
          [sectionId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Erreur lors de l’actualisation des questions');
      }
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    } catch (error) {
      console.error('Error refreshing questions:', error);
      showError('Erreur lors de l’actualisation des questions');
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  const fetchRecommendations = async (questionId, sectionId) => {
    if (showRecommendations[questionId]) {
      setShowRecommendations(prev => ({ ...prev, [questionId]: false }));
      return;
    }

    setRecommendationsLoading(prev => ({ ...prev, [questionId]: true }));
    setShowRecommendations(prev => ({ ...prev, [questionId]: true }));
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSectionQuestionsRecommendationActions(
        authTokens.access,
        id,
        sectionId,
        questionId,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setQuestionRecommendations(prev => ({
          ...prev,
          [questionId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Erreur lors du chargement des recommandations');
      }
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      showError('Erreur lors du chargement des recommandations');
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const refreshRecommendations = async (questionId, sectionId) => {
    setRecommendationsLoading(prev => ({ ...prev, [questionId]: true }));
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSectionQuestionsRecommendationActions(
        authTokens.access,
        id,
        sectionId,
        questionId,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setQuestionRecommendations(prev => ({
          ...prev,
          [questionId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Erreur lors de l’actualisation des recommandations');
      }
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    } catch (error) {
      console.error('Error refreshing recommendations:', error);
      showError('Erreur lors de l’actualisation des recommandations');
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const fetchFollowUps = async (recommendationId) => {
    if (showFollowUps[recommendationId]) {
      setShowFollowUps(prev => ({ ...prev, [recommendationId]: false }));
      return;
    }

    setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: true }));
    setShowFollowUps(prev => ({ ...prev, [recommendationId]: true }));
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSectionQuestionsRecommendationFollowups(
        authTokens.access,
        id,
        recommendationId,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setRecommendationFollowUps(prev => ({
          ...prev,
          [recommendationId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Erreur lors du chargement des suivis');
      }
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      showError('Erreur lors du chargement des suivis');
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    }
  };

  const refreshFollowUps = async (recommendationId) => {
    setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: true }));
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlanSectionQuestionsRecommendationFollowups(
        authTokens.access,
        id,
        recommendationId,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setRecommendationFollowUps(prev => ({
          ...prev,
          [recommendationId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Erreur lors de l’actualisation des suivis');
      }
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    } catch (error) {
      console.error('Error refreshing follow-ups:', error);
      showError('Erreur lors de l’actualisation des suivis');
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Clôturé' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Réalisé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPartyRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-green-100', text: 'text-green-800', label: 'Auditeur' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Audité' },
      participant: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Participant' },
      approver: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approbateur' }
    };

    const config = roleConfig[role] || roleConfig.participant;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getRiskProbabilityBadge = (probability) => {
    const probabilityConfig = {
      'critical': { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      'high': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevée' },
      'medium': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyenne' },
      'low': { bg: 'bg-green-100', text: 'text-green-800', label: 'Faible' }
    };

    const config = probabilityConfig[probability] || probabilityConfig['medium'];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    
    try {
      const result = await exportAuditPlanService.exportAuditPlanPDF(authTokens.access, id);
      
      if (result.success) {
        showSuccess('Plan d\'audit exporté avec succès');
      } else {
        showError(result.error || 'Erreur lors de l\'exportation du plan d\'audit');
      }
    } catch (error) {
      console.error('Error exporting audit plan:', error);
      showError('Erreur lors de l\'exportation du plan d\'audit');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      showSuccess(`Téléchargement de ${fileName}...`);
      const result = await publishedAuditPlanService.downloadAttachment(authTokens.access, attachmentId);
      
      if (result.success) {
        showSuccess('Fichier téléchargé avec succès');
      } else {
        showError(result.error || 'Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showError('Erreur lors du téléchargement');
    }
  };

  if (!canViewPublishedPlans) {
    return (
      <div className="text-center py-12">
        <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Accès refusé</p>
        <p className="text-sm text-gray-500 mt-2">
          Vous devez avoir le rôle AuditManager ou AuditUser et être partie prenante du programme d'audit pour consulter les détails du plan d'audit publié
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour au tableau de bord
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails du plan d'audit publié...</p>
        </div>
      </div>
    );
  }

  if (!auditPlan) {
    return (
      <div className="text-center py-12">
        <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Plan d'audit publié introuvable ou accès non autorisé</p>
        <p className="text-sm text-gray-500 mt-2">
          Vous ne pouvez consulter que les plans d'audit publiés pour lesquels vous êtes impliqué comme partie prenante du programme d'audit
        </p>
        <Link
          to="/dashboard/audit/published-plans"
          className="inline-flex items-center mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux plans publiés
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/audit/published-plans"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{auditPlan.title}</h1>
            <p className="text-sm text-gray-600 mt-1">Détails du plan d'audit publié</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Export Button */}
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Exportation...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Exporter en PDF
              </>
            )}
          </button>
          
          {getStatusBadge(auditPlan.status)}
          <div className="flex items-center text-sm text-green-600">
            <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
            Publié
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Informations de base
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Objectifs</label>
                <p className="text-sm text-gray-900">{auditPlan.objectives}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Périmètre</label>
                <p className="text-sm text-gray-900">{auditPlan.scope}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référentiels</label>
                <p className="text-sm text-gray-900">{auditPlan.reference}</p>
              </div>
              {auditPlan.note && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <p className="text-sm text-gray-900">{auditPlan.note}</p>
                </div>
              )}
            </div>
          </div>

          {/* Parties Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2 text-green-600" />
              Parties impliquées ({auditPlan.parties ? auditPlan.parties.length : 0})
            </h2>
            <div className="space-y-3">
              {auditPlan.parties && auditPlan.parties.length > 0 ? (
                auditPlan.parties.map((party) => (
                  <div
                    key={party.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{party.name}</p>
                        <p className="text-xs text-gray-500">{party.email}</p>
                      </div>
                    </div>

                    <div>
                      {getPartyRoleBadge(party.partyRole)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Aucune partie prenante</p>
                </div>
              )}
            </div>
          </div>

          {/* Risks Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-green-600" />
              Risques identifiés ({auditPlan.risks ? auditPlan.risks.length : 0})
            </h2>

            <div className="space-y-3">
              {auditPlan.risks && auditPlan.risks.length > 0 ? (
                auditPlan.risks.map((risk) => (
                  <div
                    key={risk.id}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900">{risk.name}</h3>
                      {getRiskProbabilityBadge(risk.probability)}
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{risk.description}</p>
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Conséquence :</span> {risk.consequence}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Aucun risque identifié</p>
                </div>
              )}
            </div>
          </div>

          {/* Audit Sections - Full Width */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <List className="h-5 w-5 mr-2 text-green-600" />
                Sections d'audit
              </h2>
              <div className="flex items-center space-x-2">
                {showSections && (
                  <button
                    onClick={refreshSections}
                    disabled={sectionsLoading}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Actualiser les sections"
                  >
                    <RefreshCw className={`h-4 w-4 ${sectionsLoading ? 'animate-spin' : ''}`} />
                  </button>
                )}
                <button
                  onClick={fetchSections}
                  disabled={sectionsLoading}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sectionsLoading && !showSections ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Chargement...
                    </>
                  ) : (
                    <>
                      {showSections ? (
                        <>
                          <ChevronDown className="h-4 w-4 mr-2" />
                          Masquer les sections
                        </>
                      ) : (
                        <>
                          <ChevronRight className="h-4 w-4 mr-2" />
                          Afficher les sections
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>

            {showSections && (
              <div className="mt-4">
                {sectionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-3"></div>
                      <p className="text-sm text-gray-600">Chargement des sections...</p>
                    </div>
                  </div>
                ) : sections.length > 0 ? (
                  <div className="space-y-4">
                    {sections.map((section) => (
                      <div
                        key={section.id}
                        className="border border-gray-200 rounded-lg p-5 bg-gray-50"
                      >
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            {section.section_name}
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Location */}
                            <div className="flex items-start">
                              <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-xs font-medium text-gray-500">Lieu</span>
                                <p className="text-sm text-gray-900">{section.location}</p>
                              </div>
                            </div>

                            {/* Created By */}
                            {section.created_by && (
                              <div className="flex items-start">
                                <User className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Créé par</span>
                                  <p className="text-sm text-gray-900">{section.created_by}</p>
                                </div>
                              </div>
                            )}

                            {/* Created At */}
                            <div className="flex items-start">
                              <Calendar className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-xs font-medium text-gray-500">Créé le</span>
                                <p className="text-sm text-gray-900">
                                  {new Date(section.created_at).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>

                            {/* Last Updated By */}
                            {section.last_updated_by && (
                              <div className="flex items-start">
                                <User className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Mis à jour par</span>
                                  <p className="text-sm text-gray-900">{section.last_updated_by}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Questions Section */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                              <HelpCircle className="h-4 w-4 mr-2 text-green-600" />
                              Questions d'audit
                            </h4>
                            <div className="flex items-center space-x-2">
                              {showQuestions[section.id] && sectionQuestions[section.id] && (
                                <button
                                  onClick={() => refreshQuestions(section.id)}
                                  disabled={questionsLoading[section.id]}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                                  title="Actualiser les questions"
                                >
                                  <RefreshCw className={`h-3 w-3 ${questionsLoading[section.id] ? 'animate-spin' : ''}`} />
                                </button>
                              )}
                              <button
                                onClick={() => fetchQuestions(section.id)}
                                disabled={questionsLoading[section.id]}
                                className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                              >
                                {questionsLoading[section.id] && !showQuestions[section.id] ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                    Chargement...
                                  </>
                                ) : (
                                  <>
                                    {showQuestions[section.id] ? (
                                      <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Masquer
                                      </>
                                    ) : (
                                      <>
                                        <ChevronRight className="h-3 w-3 mr-1" />
                                        Afficher
                                      </>
                                    )}
                                  </>
                                )}
                              </button>
                            </div>
                          </div>

                          {showQuestions[section.id] && (
                            <div className="mt-3">
                              {questionsLoading[section.id] ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="text-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                                    <p className="text-xs text-gray-600">Chargement des questions...</p>
                                  </div>
                                </div>
                              ) : sectionQuestions[section.id] && sectionQuestions[section.id].length > 0 ? (
                                <div className="space-y-3">
                                  {sectionQuestions[section.id].map((question) => (
                                    <div
                                      key={question.id}
                                      className="border border-gray-200 rounded-lg p-4 bg-white"
                                    >
                                      <h5 className="text-sm font-semibold text-gray-900 mb-2">
                                        {question.question_name}
                                      </h5>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                        <div>
                                          <span className="font-medium text-gray-500">Emplacement :</span>
                                          <p className="text-gray-900">{question.location}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium text-gray-500">État :</span>
                                          <p className="text-gray-900">{question.status}</p>
                                        </div>
                                        {question.requirements && (
                                          <div className="md:col-span-2">
                                            <span className="font-medium text-gray-500">Exigences :</span>
                                            <p className="text-gray-900 mt-1">{question.requirements}</p>
                                          </div>
                                        )}
                                        {question.question_description && (
                                          <div className="md:col-span-2">
                                            <span className="font-medium text-gray-500">Description :</span>
                                            <p className="text-gray-900 mt-1">{question.question_description}</p>
                                          </div>
                                        )}
                                      </div>

                                      {/* Recommendations Section */}
                                      <div className="mt-3 pt-3 border-t border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                          <h6 className="text-xs font-semibold text-gray-700 flex items-center">
                                            <Lightbulb className="h-3 w-3 mr-1 text-yellow-600" />
                                            Actions de recommandation
                                          </h6>
                                          <div className="flex items-center space-x-1">
                                            {showRecommendations[question.id] && questionRecommendations[question.id] && (
                                              <button
                                                onClick={() => refreshRecommendations(question.id, section.id)}
                                                disabled={recommendationsLoading[question.id]}
                                                className="inline-flex items-center px-2 py-0.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                                                title="Actualiser les recommandations"
                                              >
                                                <RefreshCw className={`h-3 w-3 ${recommendationsLoading[question.id] ? 'animate-spin' : ''}`} />
                                              </button>
                                            )}
                                            <button
                                              onClick={() => fetchRecommendations(question.id, section.id)}
                                              disabled={recommendationsLoading[question.id]}
                                              className="inline-flex items-center px-2 py-0.5 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors disabled:opacity-50"
                                            >
                                              {recommendationsLoading[question.id] && !showRecommendations[question.id] ? (
                                                <>
                                                  <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white mr-1"></div>
                                                  Chargement...
                                                </>
                                              ) : (
                                                <>
                                                  {showRecommendations[question.id] ? (
                                                    <>
                                                      <ChevronDown className="h-3 w-3 mr-1" />
                                                      Masquer
                                                    </>
                                                  ) : (
                                                    <>
                                                      <ChevronRight className="h-3 w-3 mr-1" />
                                                      Afficher
                                                    </>
                                                  )}
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        </div>

                                        {showRecommendations[question.id] && (
                                          <div className="mt-2">
                                            {recommendationsLoading[question.id] ? (
                                              <div className="flex items-center justify-center py-6">
                                                <div className="text-center">
                                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600 mx-auto mb-2"></div>
                                                  <p className="text-xs text-gray-600">Chargement des recommandations...</p>
                                                </div>
                                              </div>
                                            ) : questionRecommendations[question.id] && questionRecommendations[question.id].length > 0 ? (
                                              <div className="space-y-2">
                                                {questionRecommendations[question.id].map((recommendation) => (
                                                  <div
                                                    key={recommendation.id}
                                                    className="border border-yellow-200 rounded-lg p-3 bg-yellow-50"
                                                  >
                                                    <div className="text-xs space-y-2">
                                                      <div>
                                                        <span className="font-medium text-gray-500">Responsable :</span>
                                                        <p className="text-gray-900">{recommendation.owner_name || 'Non assigné'}</p>
                                                      </div>
                                                      <div>
                                                        <span className="font-medium text-gray-500">Statut :</span>
                                                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                                          recommendation.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                          recommendation.status === 'in_progress' ? 'bg-green-100 text-green-800' :
                                                          'bg-gray-100 text-gray-800'
                                                        }`}>
                                                          {recommendation.status}
                                                        </span>
                                                      </div>
                                                      <div>
                                                        <span className="font-medium text-gray-500">Date cible :</span>
                                                        <p className="text-gray-900">
                                                          {recommendation.delay_of_recommendation 
                                                            ? new Date(recommendation.delay_of_recommendation).toLocaleDateString()
                                                            : 'Non définie'
                                                          }
                                                        </p>
                                                      </div>

                                                      <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                          <span className="font-medium text-gray-500">Créé :</span>
                                                          <p className="text-gray-900">
                                                            {recommendation.created_at 
                                                              ? new Date(recommendation.created_at).toLocaleDateString()
                                                              : 'Inconnu'
                                                            }
                                                          </p>
                                                        </div>
                                                        <div>
                                                          <span className="font-medium text-gray-500">Mis à jour :</span>
                                                          <p className="text-gray-900">
                                                            {recommendation.updated_at 
                                                              ? new Date(recommendation.updated_at).toLocaleDateString()
                                                              : 'Inconnu'
                                                            }
                                                          </p>
                                                        </div>
                                                      </div>
                                                      {recommendation.elements_of_recommendation && (
                                                        <div>
                                                          <span className="font-medium text-gray-500">Éléments :</span>
                                                          <p className="text-gray-900 mt-1">{recommendation.elements_of_recommendation}</p>
                                                        </div>
                                                      )}
                                                    </div>

                                                    {/* Follow-ups Section */}
                                                    <div className="mt-2 pt-2 border-t border-yellow-300">
                                                      <div className="flex items-center justify-between mb-2">
                                                        <h6 className="text-xs font-semibold text-gray-700 flex items-center">
                                                          <TrendingUp className="h-3 w-3 mr-1 text-green-600" />
                                                          Suivis
                                                        </h6>
                                                        <div className="flex items-center space-x-1">
                                                          {showFollowUps[recommendation.id] && recommendationFollowUps[recommendation.id] && (
                                                            <button
                                                              onClick={() => refreshFollowUps(recommendation.id)}
                                                              disabled={followUpsLoading[recommendation.id]}
                                                              className="inline-flex items-center px-2 py-0.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                                                              title="Actualiser les suivis"
                                                            >
                                                              <RefreshCw className={`h-2 w-2 ${followUpsLoading[recommendation.id] ? 'animate-spin' : ''}`} />
                                                            </button>
                                                          )}
                                                          <button
                                                            onClick={() => fetchFollowUps(recommendation.id)}
                                                            disabled={followUpsLoading[recommendation.id]}
                                                            className="inline-flex items-center px-2 py-0.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                                                          >
                                                            {followUpsLoading[recommendation.id] && !showFollowUps[recommendation.id] ? (
                                                              <>
                                                                <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white mr-1"></div>
                                                                Chargement...
                                                              </>
                                                            ) : (
                                                              <>
                                                                {showFollowUps[recommendation.id] ? (
                                                                  <>
                                                                    <ChevronDown className="h-2 w-2 mr-1" />
                                                                    Masquer
                                                                  </>
                                                                ) : (
                                                                  <>
                                                                    <ChevronRight className="h-2 w-2 mr-1" />
                                                                    Afficher
                                                                  </>
                                                                )}
                                                              </>
                                                            )}
                                                          </button>
                                                        </div>
                                                      </div>

                                                      {showFollowUps[recommendation.id] && (
                                                        <div className="mt-2">
                                                          {followUpsLoading[recommendation.id] ? (
                                                            <div className="flex items-center justify-center py-4">
                                                              <div className="text-center">
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto mb-1"></div>
                                                                <p className="text-xs text-gray-600">Chargement des suivis...</p>
                                                              </div>
                                                            </div>
                                                          ) : recommendationFollowUps[recommendation.id] && recommendationFollowUps[recommendation.id].length > 0 ? (
                                                            <div className="space-y-2">
                                                              {recommendationFollowUps[recommendation.id].map((followUp) => (
                                                                <div
                                                                  key={followUp.id}
                                                                  className="border border-green-200 rounded p-2 bg-green-50"
                                                                >
                                                                  <div className="text-xs space-y-1">
                                                                    <div className="flex items-center justify-between">
                                                                      <span className="font-medium text-gray-500">Progression :</span>
                                                                      <span className="font-semibold text-green-700">{followUp.progression_percentage}%</span>
                                                                    </div>
                                                                    <div>
                                                                      <span className="font-medium text-gray-500">Statut :</span>
                                                                      <span className="ml-2 text-gray-900">{followUp.recommendation_status_selected}</span>
                                                                    </div>

                                                                    {followUp.notes && (
                                                                      <div>
                                                                        <span className="font-medium text-gray-500">Notes :</span>
                                                                        <p className="text-gray-900 mt-1">{followUp.notes}</p>
                                                                      </div>
                                                                    )}
                                                                    {followUp.attachments && followUp.attachments.length > 0 && (
                                                                      <div>
                                                                        <span className="font-medium text-gray-500">Pièces jointes :</span>
                                                                        <p className="text-gray-900">{followUp.attachments.length} fichier(s)</p>
                                                                      </div>
                                                                    )}

                                                                    <div className="text-xs text-gray-400">
                                                                      {new Date(followUp.created_at).toLocaleDateString()}
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              ))}
                                                            </div>
                                                          ) : (
                                                            <div className="text-center py-4 text-gray-500">
                                                              <TrendingUp className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                                                              <p className="text-xs">Aucun suivi pour le moment</p>
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <div className="text-center py-6 text-gray-500">
                                                <Lightbulb className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                                                <p className="text-xs">Aucune action de recommandation pour le moment</p>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  <HelpCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                  <p className="text-xs font-medium">Aucune question pour le moment</p>
                                  <p className="text-xs text-gray-400 mt-1">Les questions apparaîtront ici une fois créées</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <List className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-sm font-medium">Aucune section pour le moment</p>
                    <p className="text-xs text-gray-400 mt-1">Les sections apparaîtront ici une fois créées</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Meta Information */}
        <div className="lg:col-span-1 space-y-6">
          {/* Program Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-green-600" />
              Programme d'audit
            </h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Titre du programme</p>
                <p className="text-sm font-medium text-green-600">
                  {auditPlan.program?.title || 'Aucun programme'}
                </p>
              </div>

              {auditPlan.program?.objective && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Objectif du programme</p>
                  <p className="text-sm text-gray-900">{auditPlan.program.objective}</p>
                </div>
              )}

              {auditPlan.program?.scope && auditPlan.program.scope.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Périmètre du programme</p>
                  <div className="space-y-1">
                    {auditPlan.program.scope.map((scopeItem, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-900">
                        <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                        {scopeItem}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Publication Info Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="h-5 w-5 mr-2 text-green-600" />
              Informations de publication
            </h2>
            <div className="space-y-3">
              {auditPlan.publish_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Date de publication</p>
                    <p className="text-sm text-gray-900">
                      {new Date(auditPlan.publish_date).toLocaleDateString('fr-FR', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Location Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-green-600" />
              Lieu
            </h2>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
              <MapPin className="h-5 w-5 text-gray-600 mr-2" />
              <p className="text-sm text-gray-900">{auditPlan.location}</p>
            </div>
          </div>

          {/* Attachments Card */}
          {auditPlan.attachments && auditPlan.attachments.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-600" />
                Pièces jointes
              </h2>
              <div className="space-y-3">
                {auditPlan.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {attachment.file_size && `${(attachment.file_size / 1024).toFixed(2)} KB • `}
                          Téléchargé le {new Date(attachment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                      className="inline-flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadata Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-green-600" />
              Métadonnées
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Créé par</label>
                <p className="text-sm text-gray-900">{auditPlan.created_by}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Créé le</label>
                <p className="text-sm text-gray-900">
                  {new Date(auditPlan.created_at).toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Mis à jour par</label>
                <p className="text-sm text-gray-900">{auditPlan.last_updated_by}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Mis à jour le</label>
                <p className="text-sm text-gray-900">
                  {new Date(auditPlan.updated_at).toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}